// 직업 영웅 3D 모델(세렌·브란·루멘) — CHARACTER-PRD §2·§9. 직업 → GLB·클립 이름 매핑과 지연 로드.
// 파이프라인: Codex image_gen T포즈 시트 → Tripo → Blender(헤드리스) → Mixamo 이름 골격 → 카엘 클립 리타깃 + Mixamo 라이브러리 클립
//   (tools/blender/hero/hero_bake.py) → meshopt + WebP GLB. 손 무기는 오른손 본 자식 메시 'weapon'(스킨 아님)으로 GLB 안에 들어 있다.
// 카엘(kael.ts)·라이라(lyraLook.ts)는 기존 경로 그대로. 여기서는 새 3명만 다룬다. 모델 1회 로드 → 인스턴스는 SkeletonUtils 복제.
import * as THREE from 'three/webgpu';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { assetUrl } from '../pwa/assetMap';

/** 직업(CHARACTER-PRD §2). 검사·궁수는 카엘 리그 경로 */
export type HeroJob = 'swordsman' | 'archer' | 'mage' | 'guardian' | 'priest';
export type HeroModelId = 'seren' | 'brann' | 'lumen';

/**
 * 게임 쪽 동작 이름 → GLB 클립 이름. 카엘 GLB와 같은 이름(idle·run·atk1~3·dodge·hurt·death·victory·powerup)을 쓰고,
 * 직업 전용 시전은 skill* 이름으로 둔다. town = 마을 대기.
 */
export type HeroClipRole =
  | 'idle' | 'town' | 'run' | 'atk1' | 'atk2' | 'atk3' | 'dodge' | 'hurt' | 'death' | 'victory' | 'powerup'
  | 'skill1' | 'skill2' | 'skill3';

export interface HeroModelDef {
  readonly id: HeroModelId;
  readonly job: HeroJob;
  readonly url: string;
  /** GLB 안 신장(m, 발바닥~정수리). 카엘 1.75m 기준으로 구웠다 */
  readonly height: number;
  /** 동작 → 클립 이름(GLB에 없는 이름은 idle로 대체) */
  readonly clips: Readonly<Record<HeroClipRole, string>>;
  /** 직업 시전 3종이 무엇인지(문서·QA용) */
  readonly skillNotes: readonly [string, string, string];
}

const COMMON = { idle: 'idle', town: 'town', run: 'run', atk1: 'atk1', atk2: 'atk2', atk3: 'atk3', dodge: 'dodge', hurt: 'hurt', death: 'death', victory: 'victory', powerup: 'powerup', skill1: 'skill1', skill2: 'skill2', skill3: 'skill3' } as const;

export const HERO_MODELS: Readonly<Record<HeroModelId, HeroModelDef>> = {
  seren: { id: 'seren', job: 'mage', url: assetUrl('characters/seren_t1.glb'), height: 1.66, clips: COMMON, skillNotes: ['투사(원소탄)', '광역(지팡이 치켜듦)', '채널링(긴 시전)'] },
  brann: { id: 'brann', job: 'guardian', url: assetUrl('characters/brann_t1.glb'), height: 1.82, clips: COMMON, skillNotes: ['내려찍기(도약)', '회전', '도발'] },
  lumen: { id: 'lumen', job: 'priest', url: assetUrl('characters/lumen_t1.glb'), height: 1.64, clips: COMMON, skillNotes: ['치유', '성광(투사)', '축복(긴 시전)'] },
};

/**
 * 저장소에 GLB가 들어간 모델만 적는다(없는 파일을 레이드마다 404로 찾지 않게). 모델을 굽고
 * public/assets/characters/<id>_t1.glb를 넣는 커밋에서 여기에 id를 더한다.
 */
export const SHIPPED_MODELS: ReadonlySet<HeroModelId> = new Set<HeroModelId>(['seren', 'brann', 'lumen']);

export function heroModelShipped(id: HeroModelId): boolean { return SHIPPED_MODELS.has(id); }

/** 직업 → 모델(검사·궁수는 카엘 리그 경로라 null) */
export function modelForJob(job: HeroJob): HeroModelId | null {
  return job === 'mage' ? 'seren' : job === 'guardian' ? 'brann' : job === 'priest' ? 'lumen' : null;
}

export interface HeroModelAsset {
  readonly def: HeroModelDef;
  readonly gltf: GLTF;
  /** 이름 → 클립(골반 수평 이동을 지운 제자리 판: 위치는 게임이 정한다) */
  readonly clips: ReadonlyMap<string, THREE.AnimationClip>;
}

const cache = new Map<HeroModelId, Promise<HeroModelAsset>>();
let loader: GLTFLoader | null = null;

/**
 * 골반(Hips) 위치 트랙의 수평 성분을 첫 키 값으로 고정한다(제자리).
 * 이 리그(Mixamo → Blender 내보내기)는 Hips 노드 위에 리그 루트가 X +90°·배율 0.01로 얹혀 있어, Hips 위치 트랙의 국소 x·y가 수평이고
 * z가 높이다(−z가 위: 서 있는 골반 z ≈ −98). 예전에는 x·z를 고정해 골반 높이가 첫 키에 굳었고(달리기 발 최대 −10.6cm 침투,
 * 호흡·달리기 상하 흔들림 소실) 수평 y 이동만 남았다 — 2026-10-29 Blender 계측(qa/l1029-avatar/body/foot-audit.md).
 * lockHeight: 예전 동작(x·z 고정). 지금은 쓰는 곳이 없다(용병 카엘 GLB는 2026-10-30부터 lift 보정, 아래).
 * lift: 클립 골반 높이 보정(m, +가 위) — 국소 z에서 100·lift를 뺀다(배율 0.01, −z가 위). GLB를 고치지 않고 런타임에서 접지를 맞출 때 쓴다.
 */
export function stripRootMotion(clip: THREE.AnimationClip, lockHeight = false, lift = 0): THREE.AnimationClip {
  const out = clip.clone();
  const keep = lockHeight ? 2 : 1, dz = lockHeight ? 0 : lift * 100;
  for (const tr of out.tracks) {
    if (!/Hips\.position$/.test(tr.name)) continue;
    const v = tr.values, x0 = v[0], k0 = v[keep];
    for (let i = 0; i < v.length; i += 3) { v[i] = x0; v[i + keep] = k0; v[i + 2] -= dz; }
  }
  return out;
}

/** 모델을 한 번만 읽는다(실패하면 캐시를 비워 다음 호출에서 다시 시도) */
export function loadHeroModel(id: HeroModelId): Promise<HeroModelAsset> {
  let p = cache.get(id);
  if (!p) {
    const def = HERO_MODELS[id];
    loader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    p = loader.loadAsync(def.url).then((gltf) => {
      const clips = new Map<string, THREE.AnimationClip>();
      for (const c of gltf.animations) clips.set(c.name, stripRootMotion(c));
      return { def, gltf, clips };
    });
    p.catch(() => cache.delete(id));
    cache.set(id, p);
  }
  return p;
}

export interface HeroInstance {
  readonly root: THREE.Object3D;
  readonly skinned: THREE.SkinnedMesh | null;
  /** 손 무기 노드(없으면 null). 재질이 여럿인 무기는 GLTFLoader가 Group 아래 메시 여러 개로 만든다 */
  readonly weapon: THREE.Object3D | null;
  /** 무기 노드 아래 모든 메시(각자 재질을 지켜야 한다) */
  readonly weaponMeshes: readonly THREE.Mesh[];
  readonly mixer: THREE.AnimationMixer;
  readonly actions: ReadonlyMap<string, THREE.AnimationAction>;
}

/** 읽은 모델의 복제 인스턴스(재질은 원본 공유 — 색을 바꾸려면 호출 쪽이 교체한다) */
export function instantiateHero(asset: HeroModelAsset): HeroInstance {
  const root = cloneSkinned(asset.gltf.scene);
  let skinned: THREE.SkinnedMesh | null = null;
  let weapon: THREE.Object3D | null = null;
  root.traverse((o) => {
    if (!weapon && /^weapon/.test(o.name)) weapon = o;
    const m = o as THREE.SkinnedMesh;
    if (!m.isMesh) return;
    m.frustumCulled = false;
    if (m.isSkinnedMesh && !skinned) skinned = m;
  });
  const weaponMeshes: THREE.Mesh[] = [];
  (weapon as THREE.Object3D | null)?.traverse((o) => { if ((o as THREE.Mesh).isMesh && !(o as THREE.SkinnedMesh).isSkinnedMesh) weaponMeshes.push(o as THREE.Mesh); });
  const mixer = new THREE.AnimationMixer(root);
  const actions = new Map<string, THREE.AnimationAction>();
  for (const [name, clip] of asset.clips) actions.set(name, mixer.clipAction(clip));
  return { root, skinned, weapon, weaponMeshes, mixer, actions };
}

/** 동작 → 실제 있는 클립 이름(없으면 idle) */
export function clipName(asset: HeroModelAsset, role: HeroClipRole): string {
  const n = asset.def.clips[role];
  return asset.clips.has(n) ? n : 'idle';
}
